import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Copy, CheckCircle, AlertTriangle, Info, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CollectionConfig {
  vendor_name?: string;
  portal_url?: string;
  username?: string;
  password?: string;
  email_integration_completed?: boolean;
  forwarding_email?: string;
}

interface CollectionTabProps {
  collectionSource: 'portal' | 'email' | 'manual' | undefined;
  setCollectionSource: (source: 'portal' | 'email' | 'manual' | undefined) => void;
  collectionFrequency: string;
  setCollectionFrequency: (freq: string) => void;
  collectionConfig: CollectionConfig;
  setCollectionConfig: (config: CollectionConfig) => void;
  applyToProperty: boolean;
  setApplyToProperty: (apply: boolean) => void;
  projectId: string;
  workspaceId?: string;
}

// Hook to fetch vendors from parser_bot_suppliers
function useVendors(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['vendors', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      
      // Try to fetch from parser_bot_suppliers first
      const { data: suppliers, error } = await (supabase as any)
        .from('parser_bot_suppliers')
        .select('supplier_id, supplier_name, is_enabled')
        .eq('workspace_id', workspaceId)
        .eq('is_enabled', true)
        .order('supplier_name', { ascending: true });

      if (error) {
        console.warn('Could not fetch suppliers:', error);
        // Return fallback vendors
        return getDefaultVendors();
      }

      if (!suppliers || suppliers.length === 0) {
        return getDefaultVendors();
      }

      return suppliers.map((s: any) => ({
        id: s.supplier_id,
        name: s.supplier_name,
      }));
    },
    enabled: true,
  });
}

function getDefaultVendors() {
  return [
    { id: 'pge', name: 'PG&E' },
    { id: 'sce', name: 'Southern California Edison' },
    { id: 'sdge', name: 'SDG&E' },
    { id: 'socal_water', name: 'SoCal Water' },
    { id: 'ladwp', name: 'Los Angeles DWP' },
    { id: 'duke_energy', name: 'Duke Energy' },
    { id: 'con_edison', name: 'Con Edison' },
    { id: 'xcel_energy', name: 'Xcel Energy' },
    { id: 'national_grid', name: 'National Grid' },
    { id: 'florida_power', name: 'Florida Power & Light' },
    { id: 'dominion_energy', name: 'Dominion Energy' },
    { id: 'centerpoint', name: 'CenterPoint Energy' },
  ];
}

export const CollectionTab: React.FC<CollectionTabProps> = ({
  collectionSource,
  setCollectionSource,
  collectionFrequency,
  setCollectionFrequency,
  collectionConfig,
  setCollectionConfig,
  applyToProperty,
  setApplyToProperty,
  projectId,
  workspaceId,
}) => {
  const { toast } = useToast();
  const [emailIntegrationCompleted, setEmailIntegrationCompleted] = useState(
    collectionConfig.email_integration_completed || false
  );

  const { data: vendors = [], isLoading: loadingVendors } = useVendors(workspaceId);

  const forwardingEmail = `bills-${projectId.slice(0, 8)}@billportal.ai`;

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(forwardingEmail);
    toast({ title: 'Copied', description: 'Email address copied to clipboard' });
  };

  const handleTestConnection = () => {
    toast({ title: 'Testing...', description: 'Testing connection to portal' });
    setTimeout(() => {
      toast({ title: 'Success', description: 'Connection successful!' });
    }, 1500);
  };

  const updateConfig = (updates: Partial<CollectionConfig>) => {
    setCollectionConfig({ ...collectionConfig, ...updates });
  };

  return (
    <div className="space-y-6">
      {/* Source of Bill */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">
          Source of Bill: <span className="text-destructive">*</span>
        </Label>
        <RadioGroup
          value={collectionSource}
          onValueChange={(value) => setCollectionSource(value as 'portal' | 'email' | 'manual')}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="portal" id="portal" />
            <Label htmlFor="portal" className="font-normal">Portal Collection</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="email" id="email" />
            <Label htmlFor="email" className="font-normal">Email</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="manual" id="manual" />
            <Label htmlFor="manual" className="font-normal">Manual Upload</Label>
          </div>
        </RadioGroup>
      </div>

      {/* Bill Frequency */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          Bill Frequency: <span className="text-destructive">*</span>
        </Label>
        <Select value={collectionFrequency} onValueChange={setCollectionFrequency}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select frequency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="bi-monthly">Bi-Monthly</SelectItem>
            <SelectItem value="quarterly">Quarterly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Portal Collection Details */}
      {collectionSource === 'portal' && (
        <Card className="border-dashed">
          <CardContent className="pt-4 space-y-4">
            <h4 className="font-medium text-sm">Portal Collection Details</h4>
            
            <div className="space-y-2">
              <Label>
                Vendor Name: <span className="text-destructive">*</span>
              </Label>
              <Select
                value={collectionConfig.vendor_name || ''}
                onValueChange={(value) => updateConfig({ vendor_name: value })}
                disabled={loadingVendors}
              >
                <SelectTrigger>
                  {loadingVendors ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Loading vendors...</span>
                    </div>
                  ) : (
                    <SelectValue placeholder="Select vendor" />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="link" className="h-auto p-0 text-xs text-primary">
                + Create New Vendor
              </Button>
            </div>

            <div className="space-y-2">
              <Label>
                Portal URL: <span className="text-destructive">*</span>
              </Label>
              <Input
                placeholder="https://portal.vendor.com"
                value={collectionConfig.portal_url || ''}
                onChange={(e) => updateConfig({ portal_url: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Username: <span className="text-destructive">*</span>
              </Label>
              <Input
                placeholder="username@example.com"
                value={collectionConfig.username || ''}
                onChange={(e) => updateConfig({ username: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Password: <span className="text-destructive">*</span>
              </Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={collectionConfig.password || ''}
                onChange={(e) => updateConfig({ password: e.target.value })}
              />
            </div>

            <Button variant="outline" size="sm" onClick={handleTestConnection}>
              Test Connection
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Email Collection Details */}
      {collectionSource === 'email' && (
        <Card className="border-dashed">
          <CardContent className="pt-4 space-y-4">
            <h4 className="font-medium text-sm">Email Integration Setup</h4>
            
            <RadioGroup
              value={emailIntegrationCompleted ? 'yes' : 'no'}
              onValueChange={(value) => {
                const completed = value === 'yes';
                setEmailIntegrationCompleted(completed);
                updateConfig({ email_integration_completed: completed });
              }}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="email_yes" />
                <Label htmlFor="email_yes" className="font-normal">Yes - Email integration completed</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="email_no" />
                <Label htmlFor="email_no" className="font-normal">No - Not yet configured</Label>
              </div>
            </RadioGroup>

            <div className="space-y-2">
              <Label>Forward Bills To:</Label>
              <div className="flex gap-2">
                <Input
                  value={forwardingEmail}
                  readOnly
                  className="font-mono text-sm bg-muted"
                />
                <Button variant="outline" size="icon" onClick={handleCopyEmail}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Integration Status:</Label>
              <div className="space-y-1 text-sm">
                <p className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  Email address active
                </p>
                <p className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  Forwarding rule configured
                </p>
                <p className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="h-4 w-4" />
                  No bills received yet
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual Upload Details */}
      {collectionSource === 'manual' && (
        <Card className="border-dashed bg-muted/30">
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-start gap-2">
              <Info className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-medium text-sm">Manual Upload Selected</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  You will need to manually upload PDF bills for this account through the Bill Management interface.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  No additional configuration required.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Apply to Property Checkbox */}
      <div className="flex items-center space-x-2 pt-4 border-t">
        <Checkbox
          id="apply_property"
          checked={applyToProperty}
          onCheckedChange={(checked) => setApplyToProperty(!!checked)}
        />
        <Label htmlFor="apply_property" className="text-sm font-normal">
          Apply to all accounts under this property
        </Label>
      </div>
    </div>
  );
};
